import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Check, 
  ArrowRight,
  Code2,
  Image,
  FileArchive
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import dancingKitty from '../assets/images/dancing-kitty-white-min.gif'
import { getPricingPlans } from '../config/plans'
import { trackCTAClick, trackPlanSelected, trackBillingPeriodToggle } from '../config/analyticsEvents'

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
}

// Hero Section
const HeroSection = ({ isAuthenticated }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-primary-50/30 pt-24 pb-24">
    {/* Animated background elements */}
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary-500/5 to-purple-500/5 rounded-full blur-3xl"></div>
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
        className="text-center"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.h1 
          variants={fadeInUp}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight"
        >
          Generate Perfect App Icons
          <br />
          <span className="bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">With a Single API Call</span>
        </motion.h1>
        
        <motion.p 
          variants={fadeInUp}
          className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto mb-10"
        >
          Transform any image into iOS and Android app icons instantly. 
          Get all required sizes in a single ZIP file, ready for App Store and Play Store submission.
        </motion.p>
        
        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-20">
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              onClick={() => trackCTAClick('go_to_dashboard', 'hero')}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 cursor-pointer shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          ) : (
            <>
              <Link
                to="/auth?plan=free"
                onClick={() => trackCTAClick('get_api_key_free', 'hero')}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-200 cursor-pointer shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5"
              >
                Get API Key - Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <a
                href="#pricing"
                onClick={() => trackCTAClick('view_pricing', 'hero')}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-slate-700 bg-white rounded-xl hover:bg-slate-50 transition-all duration-200 cursor-pointer border border-slate-200 hover:border-slate-300 hover:-translate-y-0.5"
              >
                View Pricing
              </a>
            </>
          )}
        </motion.div>

        {/* Fancy Flow Diagram with Code Effect */}
        <motion.div
          variants={fadeInUp}
          className="max-w-5xl mx-auto relative"
        >
          {/* Terminal/Code style container */}
          <div className="relative rounded-2xl bg-slate-900 shadow-2xl overflow-hidden border border-slate-700">
            {/* Terminal header */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="flex items-center gap-2 text-slate-400 text-sm font-mono">
                <Code2 className="w-4 h-4" />
                <span>api.easyappicon.com</span>
              </div>
              <div className="w-16"></div>
            </div>
            
            {/* Code content area */}
            <div className="p-6 md:p-10">
              {/* Typing code effect at top */}
              <motion.div 
                className="mb-8 font-mono text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <span className="text-green-400">$</span>
                  <motion.span
                    initial={{ width: 0 }}
                    animate={{ width: "auto" }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="overflow-hidden whitespace-nowrap text-slate-300"
                  >
                    curl -X POST https://api.easyappicon.com/generate
                  </motion.span>
                  <motion.span
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="text-primary-400"
                  >|</motion.span>
                </div>
              </motion.div>

              {/* Flow diagram */}
              <div className="relative flex flex-col md:flex-row items-center justify-between gap-8 md:gap-6">
                {/* Animated data flow line - Left side (Your Image to API Processing) */}
                <div className="hidden md:block absolute top-1/2 left-[15%] right-[58%] -translate-y-1/2 h-0.5 z-0">
                  <div className="h-full bg-slate-700 rounded-full"></div>
                  <motion.div 
                    className="absolute top-0 h-full w-12 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full"
                    animate={{ 
                      left: ['-15%', '115%'],
                      opacity: [0, 1, 1, 0]
                    }}
                    transition={{ 
                      duration: 1, 
                      repeat: Infinity, 
                      ease: "linear",
                      repeatDelay: 3
                    }}
                  />
                </div>
                
                {/* Animated data flow line - Right side (API Processing to App Icons) */}
                <div className="hidden md:block absolute top-1/2 left-[58%] right-[15%] -translate-y-1/2 h-0.5 z-0">
                  <div className="h-full bg-slate-700 rounded-full"></div>
                  <motion.div 
                    className="absolute top-0 h-full w-12 bg-gradient-to-r from-transparent via-green-500 to-transparent rounded-full"
                    animate={{ 
                      left: ['-15%', '115%'],
                      opacity: [0, 1, 1, 0]
                    }}
                    transition={{ 
                      duration: 1, 
                      repeat: Infinity, 
                      ease: "linear",
                      delay: 2,
                      repeatDelay: 3
                    }}
                  />
                </div>
                
                {/* Step 1: Input - Request */}
                <motion.div
                  className="group relative z-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ scale: 1.03 }}
                >
                  <div className="flex flex-col items-center">
                    <div className="relative mb-4">
                      {/* Card */}
                      <div className="w-32 h-32 rounded-xl bg-slate-800 border border-slate-600 shadow-xl group-hover:border-blue-500/50 transition-colors flex items-center justify-center">
                        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center">
                          <Image className="w-8 h-8 text-blue-400" />
                        </div>
                      </div>
                      {/* Badge */}
                      <div className="absolute -top-2 -right-2 px-2 py-1 rounded-md bg-blue-500 text-white text-xs font-bold shadow-lg">
                        POST
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-white">Your Image</span>
                    <span className="text-xs text-slate-400 mt-1 font-mono">base64 image</span>
                  </div>
                </motion.div>

                {/* Arrow 1 */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex-shrink-0 hidden md:block"
                >
                  <ArrowRight className="w-7 h-7 text-slate-600" />
                </motion.div>
                <motion.div className="md:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <ArrowRight className="w-7 h-7 text-slate-600 rotate-90" />
                </motion.div>

                {/* Step 2: Processing - Dancing Kitty */}
                <motion.div
                  className="group relative z-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  whileHover={{ scale: 1.03 }}
                >
                  <div className="flex flex-col items-center">
                    <div className="relative mb-4">
                      {/* Animated flow border - only animates when flow arrives */}
                      <svg className="absolute -inset-1 w-[calc(100%+8px)] h-[calc(100%+8px)]" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="48"
                          fill="none"
                          stroke="#334155"
                          strokeWidth="1"
                        />
                        <motion.circle
                          cx="50"
                          cy="50"
                          r="48"
                          fill="none"
                          stroke="#f97316"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeDasharray="301.6"
                          strokeDashoffset="301.6"
                          animate={{
                            strokeDashoffset: [301.6, 301.6, 0, 0, 301.6],
                          }}
                          transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "linear",
                            times: [0, 0.25, 0.5, 0.55, 0.55],
                          }}
                          style={{ transformOrigin: 'center', rotate: '162deg' }}
                        />
                      </svg>
                      {/* Main circle */}
                      <div className="relative w-40 h-40 rounded-full bg-slate-800 flex items-center justify-center shadow-xl shadow-primary-500/20 overflow-hidden">
                        <img 
                          src={dancingKitty} 
                          alt="Processing" 
                          className="w-28 h-28 object-contain"
                        />
                      </div>
                      {/* Processing indicator */}
                      <motion.div 
                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-mono flex items-center gap-1"
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >⚙</motion.span>
                        processing
                      </motion.div>
                      {/* Floating code snippets */}
                      <motion.div
                        className="absolute -top-2 -left-4 px-2 py-1 rounded bg-slate-700 text-[10px] font-mono text-green-400"
                        animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        resize()
                      </motion.div>
                      <motion.div
                        className="absolute -top-1 -right-6 px-2 py-1 rounded bg-slate-700 text-[10px] font-mono text-cyan-400"
                        animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                      >
                        optimize()
                      </motion.div>
                      <motion.div
                        className="absolute -bottom-6 -right-2 px-2 py-1 rounded bg-slate-700 text-[10px] font-mono text-yellow-400"
                        animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                      >
                        generate()
                      </motion.div>
                    </div>
                    <span className="text-sm font-semibold text-white mt-4">EasyAppIcon API</span>
                  </div>
                </motion.div>                {/* Arrow 2 */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="flex-shrink-0 hidden md:block"
                >
                  <ArrowRight className="w-7 h-7 text-slate-600" />
                </motion.div>
                <motion.div className="md:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <ArrowRight className="w-8 h-8 text-slate-600 rotate-90" />
                </motion.div>

                {/* Step 3: Output - Response */}
                <motion.div
                  className="group relative z-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  whileHover={{ scale: 1.03 }}
                >
                  <div className="flex flex-col items-center">
                    <div className="relative mb-4">
                      {/* Card */}
                      <div className="w-32 h-32 rounded-xl bg-slate-800 border border-slate-600 shadow-xl group-hover:border-green-500/50 transition-colors flex items-center justify-center">
                        <motion.div 
                          className="w-16 h-16 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center"
                          animate={{ y: [0, -3, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <FileArchive className="w-8 h-8 text-green-400" />
                        </motion.div>
                      </div>
                      {/* Badge */}
                      <div className="absolute -top-2 -right-2 px-2 py-1 rounded-md bg-green-500 text-white text-xs font-bold shadow-lg">
                        200 OK
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-white">App Icons</span>
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-300 font-mono">iOS</span>
                      <span className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-300 font-mono">Android</span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Response code at bottom */}
              <motion.div 
                className="mt-8 font-mono text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <div className="flex items-start gap-2 text-slate-500">
                  <span className="text-green-400">✓</span>
                  <span className="text-slate-400">Response: </span>
                  <span className="text-green-400">icons.zip</span>
                  <span className="text-slate-500">ready</span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
)

// Stats Section - Files Processed Counter
const stats = [
  { value: '500K+', label: 'Icons Generated', description: 'And counting every day' },
  { value: '30K+', label: 'Happy Users', description: 'Using our API worldwide' },
  { value: '99.9%', label: 'Uptime', description: 'Reliable infrastructure' },
  { value: '<1s', label: 'Avg Response Time', description: 'Lightning fast processing' }
]

const StatsSection = () => (
  <section className="py-20 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden" id="stats">
    {/* Background decorations */}
    <div className="absolute top-0 left-0 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
    <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <motion.span 
          className="inline-block px-4 py-2 rounded-full bg-primary-500/10 text-primary-600 text-sm font-semibold mb-4"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          Trusted by Users
        </motion.span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
          Powering Apps{' '}
          <span className="bg-gradient-to-r from-primary-500 to-emerald-500 bg-clip-text text-transparent">
            Worldwide
          </span>
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Join thousands of users who rely on EasyAppIcon for their icon generation needs.
        </p>
      </motion.div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className="group relative"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="relative p-8 rounded-3xl bg-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 hover:-translate-y-1 text-center">
              <motion.div 
                className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-500 to-emerald-500 bg-clip-text text-transparent mb-2"
                initial={{ scale: 0.5 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
              >
                {stat.value}
              </motion.div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">{stat.label}</h3>
              <p className="text-sm text-slate-500">{stat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
)

// Code Preview Section
const codeExample = `const response = await fetch('https://api.easyappicon.com/generate', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    uploadedImage: 'data:image/png;base64,YOUR_BASE64_IMAGE',
    showBadge: false,
    format: 'png',
    ...
  })
});

// Returns ZIP file with all iOS & Android icon sizes`

const integrationFeatures = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
    ),
    title: 'All iOS Sizes',
    description: '20x20 to 1024x1024 pixels'
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.4-.59-2.96-.92-4.47-.92s-3.07.33-4.47.92L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
      </svg>
    ),
    title: 'All Android Sizes',
    description: 'Adaptive icons, mdpi to xxxhdpi'
  },
  {
    icon: FileArchive,
    title: 'Badge Support',
    description: 'Add custom text overlays to icons'
  },
  {
    icon: Image,
    title: 'Multiple Formats',
    description: 'PNG, WebP & more output options'
  }
]

const CodePreviewSection = () => (
  <section className="py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
    {/* Animated background grid */}
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
    
    {/* Glowing orbs */}
    <div className="absolute top-20 left-20 w-72 h-72 bg-primary-500/20 rounded-full blur-[100px] animate-pulse"></div>
    <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }}></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px]"></div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <motion.span 
          className="inline-block px-4 py-2 rounded-full bg-white/10 text-primary-400 text-sm font-semibold mb-4 backdrop-blur-sm border border-white/10"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <Code2 className="w-4 h-4 inline mr-2" />
          Developer Experience
        </motion.span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
          Simple{' '}
          <span className="bg-gradient-to-r from-primary-400 to-emerald-400 bg-clip-text text-transparent">
            Integration
          </span>
        </h2>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto">
          Our RESTful API is designed for simplicity. Send an image, get back a ZIP file 
          with all the icon sizes you need.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Feature Cards */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-2 gap-4"
        >
          {integrationFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="group relative p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-primary-500/50 hover:bg-white/10 transition-all duration-300 cursor-pointer backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-emerald-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 text-primary-400">
                  {typeof feature.icon === 'function' && feature.icon.prototype?.render === undefined 
                    ? <feature.icon /> 
                    : <feature.icon className="w-6 h-6" />}
                </div>
                <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Code Block */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/30 to-emerald-500/30 rounded-3xl blur-2xl opacity-50"></div>
          <div className="relative bg-slate-900/80 rounded-2xl overflow-hidden shadow-2xl border border-white/10 backdrop-blur-xl">
            <div className="flex items-center justify-between px-5 py-4 bg-slate-800/50 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400 shadow-lg shadow-red-400/50"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50"></div>
                <div className="w-3 h-3 rounded-full bg-green-400 shadow-lg shadow-green-400/50"></div>
              </div>
              <span className="text-sm text-slate-400 font-mono flex items-center gap-2">
                <Code2 className="w-4 h-4" />
                example.js
              </span>
            </div>
            <pre className="p-6 overflow-x-auto text-sm">
              <code className="text-green-400 font-mono whitespace-pre leading-relaxed">{codeExample}</code>
            </pre>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
)

const PricingSection = ({ isAuthenticated }) => {
  const [isYearly, setIsYearly] = useState(true)
  const plans = getPricingPlans(isYearly)

  return (
  <section className="py-28 bg-gradient-to-b from-slate-50 via-slate-100 to-slate-50 relative overflow-hidden" id="pricing">
    {/* Background decorations */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary-500/5 to-emerald-500/5 rounded-full blur-3xl"></div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <motion.span 
          className="inline-block px-4 py-2 rounded-full bg-green-500/10 text-green-600 text-sm font-semibold mb-4"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          Pricing
        </motion.span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
          Simple,{' '}
          <span className="bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">
            Transparent
          </span>{' '}
          Pricing
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
          Start free, scale as you grow. No hidden fees, no surprises.
        </p>
        
        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4">
          <span className={`text-sm font-medium ${!isYearly ? 'text-slate-900' : 'text-slate-500'}`}>
            Monthly
          </span>
          <button
            onClick={() => { setIsYearly(!isYearly); trackBillingPeriodToggle(!isYearly ? 'yearly' : 'monthly') }}
            className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
              isYearly ? 'bg-primary-500' : 'bg-slate-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 ${
                isYearly ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${isYearly ? 'text-slate-900' : 'text-slate-500'}`}>
            Yearly
          </span>
          {isYearly && (
            <span className="ml-2 px-2 py-1 text-xs font-semibold text-emerald-600 bg-emerald-100 rounded-full">
              Save 40%
            </span>
          )}
        </div>
      </motion.div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.name}
            className="relative group"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
          >
            {plan.popular && (
              <>
                <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-emerald-500 to-primary-500 rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-300 animate-pulse"></div>
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10">
                  <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg shadow-red-500/30">
                    🔥 Limited Time Promo
                  </span>
                </div>
              </>
            )}
            
            <div className={`relative p-8 rounded-3xl h-full transition-all duration-300 ${
              plan.popular 
                ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl' 
                : 'bg-white text-slate-900 border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:-translate-y-1'
            }`}>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold">{plan.name}</h3>
                  {plan.savings && (
                    <span className="inline-flex items-center px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-green-500 rounded-full shadow-lg">
                      {plan.savings}
                    </span>
                  )}
                </div>
                <p className={plan.popular ? 'text-slate-400' : 'text-slate-600'}>
                  {plan.description}
                </p>
              </div>
              
              <div className="mb-8">
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold">{plan.price}</span>
                  <span className={`text-lg ${plan.popular ? 'text-slate-400' : 'text-slate-600'}`}>
                    {plan.period}
                  </span>
                </div>
                {plan.periodNote && (
                  <p className="text-emerald-400 text-sm font-bold mt-2">
                    {plan.periodNote}
                  </p>
                )}
              </div>
              
              <ul className="space-y-4 mb-10">
                {plan.features.map((feature, i) => (
                  <motion.li 
                    key={i} 
                    className="flex items-center"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                      plan.popular ? 'bg-primary-500/20' : 'bg-green-500/10'
                    }`}>
                      <Check className={`w-4 h-4 ${
                        plan.popular ? 'text-primary-400' : 'text-green-500'
                      }`} />
                    </div>
                    <span className={plan.popular ? 'text-slate-300' : 'text-slate-600'}>
                      {feature}
                    </span>
                  </motion.li>
                ))}
              </ul>
              
              {isAuthenticated ? (
                plan.popular && (
                  <Link
                    to="/pricing"
                    onClick={() => trackCTAClick('manage_plan', 'landing_pricing')}
                    className="block w-full py-4 px-6 text-center font-semibold rounded-xl transition-all duration-300 cursor-pointer bg-gradient-to-r from-primary-500 to-emerald-500 text-white hover:from-primary-600 hover:to-emerald-600 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5"
                  >
                    Manage Plan
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                  </Link>
                )
              ) : (
                <Link
                  to={`/auth?plan=${plan.name.toLowerCase()}`}
                  onClick={() => trackPlanSelected(plan.name, isYearly ? 'yearly' : 'monthly')}
                  className={`block w-full py-4 px-6 text-center font-semibold rounded-xl transition-all duration-300 cursor-pointer ${
                    plan.popular
                      ? 'bg-gradient-to-r from-primary-500 to-emerald-500 text-white hover:from-primary-600 hover:to-emerald-600 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5'
                      : 'bg-slate-900 text-white hover:bg-slate-800 hover:-translate-y-0.5'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  )
}

// Main Landing Page Component
const LandingPage = () => {
  const { user } = useAuth()
  const isAuthenticated = !!user

  useEffect(() => {
    document.title = 'EasyAppIcon API - App Icon Generation API for iOS & Android'
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Generate perfect iOS and Android app icons with a single API call. Fast, secure, and developer-friendly icon generation service.')
    }
  }, [])

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection isAuthenticated={isAuthenticated} />
        <CodePreviewSection />
        <StatsSection />
        <PricingSection isAuthenticated={isAuthenticated} />
      </main>
      <Footer />
    </div>
  )
}

export default LandingPage
